import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { authApi } from './api';
import { useAuth } from '@/lib/auth/useAuth';
import { useApiError } from '@/lib/query/useApiError';
import { useLocale } from '@/lib/i18n/useLocale';
import { Card } from '@/lib/ui/Card';
import { PageHeader, Eyebrow } from '@/lib/ui/Typography';
import { TextInput } from '@/lib/ui/Field';
import { Select } from '@/lib/ui/Select';
import { Button } from '@/lib/ui/Button';
import { useToast } from '@/lib/ui';
import { PasswordStrength } from './PasswordStrength';
import type { Locale } from '@/lib/api/types';

/** Self-service account: profile, password, and the TOTP enroll→verify→disable flow (spec §15.1a). */
export function AccountPage() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const { setLocale } = useLocale();
  const onError = useApiError();
  const toast = useToast();

  const [name, setName] = useState(user?.name ?? '');
  const [locale, setLoc] = useState<Locale>(user?.locale ?? 'en');
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');

  if (!user) return null;

  async function saveProfile() {
    try {
      await authApi.updateMe({ name, locale });
      setLocale(locale);
      await refreshUser();
      toast.success(t('account.profileUpdated'));
    } catch (e) {
      onError(e);
    }
  }

  async function changePassword() {
    try {
      await authApi.changePassword({ current_password: curPw, new_password: newPw });
      setCurPw('');
      setNewPw('');
      toast.success(t('account.passwordChanged'));
    } catch (e) {
      onError(e);
    }
  }

  return (
    <div className="flex flex-col gap-lg">
      <PageHeader eyebrow={t('account.eyebrow')} title={t('account.title')} />

      <Card>
        <Eyebrow>{t('account.profile')}</Eyebrow>
        <div className="mt-md flex flex-col gap-md">
          <TextInput label={t('account.name')} value={name} onChange={(e) => setName(e.target.value)} />
          <Select
            label={t('account.interfaceLanguage')}
            value={locale}
            onValueChange={(v) => setLoc(v as Locale)}
            options={[
              { value: 'en', label: 'English' },
              { value: 'ar', label: 'العربية' },
            ]}
          />
          <div>
            <Button onClick={saveProfile}>{t('account.saveProfile')}</Button>
          </div>
        </div>
      </Card>

      <Card>
        <Eyebrow>{t('account.changePassword')}</Eyebrow>
        <div className="mt-md flex flex-col gap-md">
          <TextInput
            type="password"
            label={t('account.currentPassword')}
            autoComplete="current-password"
            value={curPw}
            onChange={(e) => setCurPw(e.target.value)}
          />
          <TextInput
            type="password"
            label={t('account.newPassword')}
            autoComplete="new-password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
          />
          <PasswordStrength value={newPw} />
          <div>
            <Button onClick={changePassword} disabled={!curPw || newPw.length < 10}>
              {t('account.changePassword')}
            </Button>
          </div>
        </div>
      </Card>

      <MfaCard mfaEnabled={user.mfa_enabled} onChanged={refreshUser} />
    </div>
  );
}

function MfaCard({ mfaEnabled, onChanged }: { mfaEnabled: boolean; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const onError = useApiError();
  const toast = useToast();
  const [enroll, setEnroll] = useState<{ secret: string; qr: string } | null>(null);
  const [code, setCode] = useState('');

  async function begin() {
    try {
      const { secret, otpauth_url } = await authApi.mfaEnroll();
      const qr = await QRCode.toDataURL(otpauth_url);
      setEnroll({ secret, qr });
    } catch (e) {
      onError(e);
    }
  }
  async function verify() {
    try {
      await authApi.mfaVerify(code);
      setEnroll(null);
      setCode('');
      await onChanged();
      toast.success(t('account.mfaEnabledToast'));
    } catch (e) {
      onError(e);
    }
  }
  async function disable() {
    try {
      await authApi.mfaDisable(code);
      setCode('');
      await onChanged();
      toast.success(t('account.mfaDisabledToast'));
    } catch (e) {
      onError(e);
    }
  }

  return (
    <Card>
      <Eyebrow>{t('account.mfaTitle')}</Eyebrow>
      <p className="mt-xs text-small text-ink-mid">
        {mfaEnabled ? t('account.mfaEnabledHint') : t('account.mfaSetupHint')}
      </p>
      <div className="mt-md flex flex-col gap-md">
        {!mfaEnabled && !enroll && <Button onClick={begin}>{t('account.beginEnrollment')}</Button>}
        {!mfaEnabled && enroll && (
          <>
            <img src={enroll.qr} alt={t('account.qrAlt')} className="h-[160px] w-[160px]" />
            <p className="font-mono text-small text-ink-mute">{t('account.enterSecret', { secret: enroll.secret })}</p>
            <TextInput label={t('account.code6')} inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} />
            <div className="flex gap-sm">
              <Button onClick={verify} disabled={code.length < 6}>
                {t('account.verifyAndEnable')}
              </Button>
              <Button variant="ghost" onClick={() => setEnroll(null)}>
                {t('common.cancel')}
              </Button>
            </div>
          </>
        )}
        {mfaEnabled && (
          <>
            <TextInput label={t('account.code6Disable')} inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} />
            <div>
              <Button variant="danger" onClick={disable} disabled={code.length < 6}>
                {t('account.disableMfa')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
